import * as React from 'react';
import { Button, Tooltip, Tree, TreeItem, TreeItemLayout, Spinner } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
// import CommonLoading from '../CommonLoading/CommonLoading';

export type TreeNode = {
    Key: string;
    Name: string;
    Children?: TreeNode[];
    tagName?: string;
};

const sortTreeData = (nodes: TreeNode[]): TreeNode[] => {
    if (nodes?.length === 0) {
        return [];
    }
    return nodes
        .slice()
        .sort((a, b) => a.Name.localeCompare(b.Name))
        .map(node => ({
            ...node,
            tree: node.Children?.length > 0 ? sortTreeData(node.Children) : node.Children,
        }));
};

/**
 * Filters the tree node array and returns a new tree structure of the matching nodes and their ancestor and descendant nodes,
 * as well as an array of IDs of all branch nodes.
 * @param nodes TreeNode Array
 * @param filterText Text for fuzzy matching
 * @returns An object containing the filtered TreeNode array and the branch node ID array
 */
function filterTreeNodes(
    nodes: TreeNode[],
    filterText: string
): { tree: TreeNode[]; matchedNodeParentIds: string[] } {

    if (!filterText) {
        return {
            tree: nodes,
            matchedNodeParentIds: []
        };
    }

    const result: TreeNode[] = [];
    const matchedNodeParentIds: string[] = [];

    /**
     * Recursive filtering nodes
     * @param node current node
     * @param parentMatch is parent node match
     * @param parentNode The parent node of the current node
     * @returns Returns a new TreeNode if the current node or its children match, otherwise returns null
     */
    function filterNode(
        node: TreeNode,
        parentMatch: boolean = false,
        parentNode: TreeNode | null = null
    ): TreeNode | null {
        debugger
        const isMatch = node.Name.toLowerCase().includes(filterText.toLowerCase());
        let filteredChildren: TreeNode[] | undefined = undefined;
        let childMatch = false; // Flag to indicate if any child matches

        if (node.Children) {
            filteredChildren = node.Children
                .map(child => filterNode(child, isMatch || parentMatch, node)) // Pass the current node as the parentNode
                .filter(child => child !== null) as TreeNode[];

            childMatch = filteredChildren.length > 0; // Check if any child matches
        }

        if (isMatch || parentMatch) {
            // If the current node matches or the parent matches, return a new node with filtered children
            const newNode: TreeNode = {
                Key: node.Key,
                Name: node.Name,
                tagName: node.tagName,
            };

            if (node.Children) {
                newNode.Children = filteredChildren;
            }

            // If the current node matches and has a parent, add the parent's ID to matchedNodeParentIds
            if (isMatch && parentNode) {
                matchedNodeParentIds.push(parentNode.Key);
            }

            return newNode;
        } else if (childMatch) {
            // If the current node doesn't match, but has matching children, add the current node's ID to matchedNodeParentIds
            matchedNodeParentIds.push(node.Key);

            const newNode: TreeNode = {
                Key: node.Key,
                Name: node.Name,
                tagName: node.tagName,
                Children: filteredChildren, // Include only matching child nodes
            };
            return newNode;
        }

        return null;
    }

    // Traverse all nodes and filter nodes
    nodes.forEach(node => {
        const filteredNode = filterNode(node);
        if (filteredNode) {
            result.push(filteredNode);
        }
    });

    return { tree: result, matchedNodeParentIds: Array.from(new Set(matchedNodeParentIds)) };
}

const renderTreeNodes = (
    nodes: TreeNode[],
    callback: (node: TreeNode) => void,
    openItems: string[],
    onOpenChange: (e: React.SyntheticEvent, data: { value: string; open: boolean }) => void,
    ancestors: string = ''
): React.JSX.Element[] => {
    return nodes.map((node) => {
        // construct the full name of the current node: If the parent name exists, connect it with /
        const fullName = ancestors ? `${ancestors}/${node.Name}` : node.Name;
        const isBranch = node.Children && node.Children.length > 0;

        const currentLevel = ancestors ? ancestors.split('/').length + 1 : 1;

        return (
            <TreeItem
                key={node.Key}
                value={node.Key}
                itemType={isBranch ? "branch" : "leaf"}
                open={openItems.includes(node.Key)}
                onOpenChange={(e, data) => onOpenChange(e, { value: node.Key, open: data.open })}
            >
                <TreeItemLayout
                    onDoubleClick={
                        currentLevel === 3
                            ? () => {
                                callback({ ...node, tagName: fullName });
                            }
                            : undefined
                    }
                    actions={
                        currentLevel === 3 ? (
                            <Tooltip content="Add" relationship="label">
                                <Button
                                    appearance="subtle"
                                    icon={<AddRegular />}
                                    onClick={() => {
                                        callback({ ...node, tagName: fullName });
                                    }}
                                />
                            </Tooltip>
                        ) : null
                    }
                >
                    {node.Name}
                </TreeItemLayout>
                {/* if has tree，recursively render child nodes, passing the full name of the current node as the parent's cumulative name */}
                {isBranch && <Tree>{renderTreeNodes(node.Children!, callback, openItems, onOpenChange, fullName)}</Tree>}
            </TreeItem>
        );
    });
};

interface ISelectableTreeProps {
    treeData: TreeNode[];
    onItemAdd: (item: TreeNode) => void;
    filterText?: string;
    isLoading: boolean;
}

export const SelectableTree: React.FC<ISelectableTreeProps> = (props: ISelectableTreeProps) => {

    const sortedSourceData = React.useMemo(() => {
        return sortTreeData(props.treeData);
    }, [props.treeData]);

    const { tree, matchedNodeParentIds } = React.useMemo(() => {
        return filterTreeNodes(sortedSourceData, props.filterText);
    }, [sortedSourceData, props.filterText]);

    const [openItems, setOpenItems] = React.useState<string[]>([]);

    React.useEffect(() => {
        setOpenItems(matchedNodeParentIds || [])
    }, [matchedNodeParentIds]);

    const handleOpenChange = (e: React.SyntheticEvent, data: { value: string; open: boolean }) => {
        const { value, open } = data;
        setOpenItems(prevOpenItems => {
            if (open) {
                return [...prevOpenItems, value];
            } else {
                return prevOpenItems.filter(item => item !== value);
            }
        });
    };

    return (
        <div style={{ width: '100%', height: '100%' }}>
            {
                props.isLoading ?
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Spinner label="Loading tree..." />
                    </div>
                    : 
                    <Tree aria-label="Primary Products tree" size="small">
                        {renderTreeNodes(tree, props.onItemAdd, openItems, handleOpenChange)}
                    </Tree>
            }

        </div>
    );
};